<template>
  <div class="TheTopbar">
    <div v-if="showActionButtons" class="TheTopbar__icon-container">
      <UITooltip>
        <template #trigger>
          <UIButton
            class="TheTopbar__icon"
            theme="tertiary"
            size="md"
            leadIcon="sidebarFlipRegular"
            aria-label="Toggle sidebar"
            @click="$emit('toggleSidebar')"
          />
        </template>
        <template #content>
          <TooltipContentKeyboardShortcut>
            <div>Open sidebar</div>
            <UIKeyboardKey
              :theme="'dark'"
              :macKeys="['cmd', 'shift', 'S']"
              :winKeys="['ctrl', 'shift', 'S']"
            />
          </TooltipContentKeyboardShortcut>
        </template>
      </UITooltip>

      <UITooltip>
        <template #trigger>
          <UIButton
            class="TheTopbar__icon"
            theme="tertiary"
            size="md"
            :to="{ name: 'NewChat' }"
            leadIcon="pencilSquare"
            aria-label="New chat"
          />
        </template>

        <template #content>
          <TooltipContentKeyboardShortcut>
            <div>New chat</div>
            <UIKeyboardKey
              :theme="'dark'"
              :macKeys="['cmd', 'N']"
              :winKeys="['ctrl', 'N']"
            />
          </TooltipContentKeyboardShortcut>
        </template>
      </UITooltip>
    </div>

    <div v-if="gpt && !noGptFound" class="TheTopbar__gpt-selector">
      <UIGradientSphere
        class="TheTopbar__sphere"
        :gradient="gpt.gradient"
        size="sm"
      />
      <div class="TheTopbar__gpt-selector-name">
        {{ gpt.name }}
      </div>
      <GptContextMenu
        class="TheTopbar__gpt-selector-menu"
        :gpt="gpt"
        :isMenuVisible="true"
      />
    </div>
    <DropdownModel v-if="showModalSelector" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { UIButton, UIKeyboardKey, UITooltip } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useGptQueries } from '@/apiClient/queries/useGptQueries'
import { useChatStore } from '@/store/chat'
import GptContextMenu from '@/views/pages/Gpt/List/components/GptContextMenu.vue'

import TooltipContentKeyboardShortcut from '../misc/TooltipContentKeyboardShortcut.vue'
import DropdownModel from './components/dropdowns/DropdownModel.vue'
import UIGradientSphere from '../ui/misc/UIGradientSphere.vue'

defineEmits(['toggleSidebar'])
defineProps<{
  showActionButtons?: boolean
}>()

const route = useRoute()
const { placeholderChat, activeChat } = storeToRefs(useChatStore())
const gptUuid = computed(
  () =>
    (route.query.gptUuid as string | undefined) ||
    activeChat.value?.gpt_id ||
    placeholderChat.value?.gpt_id,
)
const {
  data: gpt,
  isLoading: isLoadingGpt,
  error,
} = useGptQueries().get(gptUuid)

const noGptFound = computed(() => error.value?.status === 404)

const showModalSelector = computed(
  () =>
    route.meta.showModalSelector &&
    (!gpt.value || noGptFound.value) &&
    !isLoadingGpt.value,
)
const breadcrumb = computed(() => route.meta.breadcrumb)
</script>

<style scoped lang="scss">
.TheTopbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: var(--bg-primary);
  padding: 0.75rem;
  height: $topbar-height;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border-light);
  z-index: 98;

  .TheTopbar__icon-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .TheTopbar__logo-link {
    display: flex;
    text-decoration: none;
    align-items: center;
  }

  .TheTopbar__logo-link-text {
    font-size: 1.25rem;
    text-decoration: none;
    color: var(--text-accent);
    font-weight: 600;
  }

  .TheTopbar__logo-link-logo {
    --size: 1.25rem;
    margin-right: 0.5rem;
  }

  .TheTopbar__divider {
    margin: 0 0.25rem;
    height: 1.25rem;
  }

  .TheTopbar__cog-icon {
    --size: 1.25rem;
    --color: var(--icon-primary);
    width: 2.25rem;
    height: 2.25rem;
  }

  .TheTopbar__icon.UIButton {
    --text-color: var(--icon-tertiary, #747481);
  }

  .TheTopbar__gpt-selector {
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>

<style lang="scss">
.TheTopbar__icon-shortcut {
  text-align: center;
  color: var(--text-tertiary);
}

.TheTopbar__gpt-selector-menu {
  --gpt-context-menu-icon-size: 1rem;
}

.TheTopbar__sphere {
  .UIGradientSphere__sphere--sm {
    --sphere-size: 1.5rem !important;
  }
}
</style>
